/**
 * Provider Form Fields
 * Dynamic form fields based on provider type configuration
 */

import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthProviderForm, ProviderTemplate } from '../types';

interface ProviderFormFieldsProps {
  providerType: string;
  config: AuthProviderForm['config'];
  onChange: (key: string, value: string | boolean) => void;
  template: ProviderTemplate;
}

/**
 * Renders dynamic form fields based on the selected provider template
 */
export const ProviderFormFields: React.FC<ProviderFormFieldsProps> = ({
  providerType,
  config,
  onChange,
  template,
}) => {
  const { t } = useTranslation();

  const getRedirectUri = () => {
    const baseUrl =
      import.meta.env.VITE_API_BASE_URL ||
      (import.meta.env.PROD
        ? `${window.location.protocol}//${window.location.host}`
        : 'http://localhost:8080');
    return `${baseUrl}/api/auth/callback/entra`;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('auth.providers.fields.configuration')}
      </Typography>
      <Box sx={{ display: 'grid', gap: 3 }}>
        {template.fields.map((field) => (
          <div key={field.key}>
            {field.type === 'boolean' ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(config[field.key as keyof typeof config])}
                    onChange={(e) => onChange(field.key, e.target.checked)}
                  />
                }
                label={field.label}
              />
            ) : (
              <TextField
                fullWidth
                label={field.label}
                type={field.type}
                value={config[field.key as keyof typeof config] || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
                required={field.required}
                helperText={
                  field.key === 'redirect_uri'
                    ? t('auth.providers.redirectUriHelper', {
                        uri: getRedirectUri(),
                      })
                    : undefined
                }
              />
            )}
          </div>
        ))}
      </Box>

      {providerType === 'entra' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>{t('auth.providers.entraSetup.title')}</strong>
          </Typography>
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            <li>{t('auth.providers.entraSetup.step1')}</li>
            <li>{t('auth.providers.entraSetup.step2')}</li>
            <li>{t('auth.providers.entraSetup.step3')}</li>
            <li>{t('auth.providers.entraSetup.step4')}</li>
            <li>{t('auth.providers.entraSetup.step5')}</li>
            <li>{t('auth.providers.entraSetup.step6')}</li>
          </ol>
        </Alert>
      )}
    </Box>
  );
};
